import { useState, useRef, useEffect, useCallback } from 'react';
import { clsx } from 'clsx';

interface FilterOption {
  id: string;
  label: string;
}

interface ActiveFilter {
  key: string;
  label: string;
  value: string;
  displayValue: string;
}

interface FilterGroup {
  key: string;
  label: string;
  options: FilterOption[];
}

interface CatalogSearchFilterProps {
  search: string;
  onSearchChange: (value: string) => void;
  activeFilters: ActiveFilter[];
  onFilterAdd: (filter: ActiveFilter) => void;
  onFilterRemove: (key: string) => void;
  onFiltersClear: () => void;
  filterGroups: FilterGroup[];
}

function ChevronDownIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 20 20" strokeWidth={1.5} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" />
    </svg>
  );
}

function SearchIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 20 20" strokeWidth={1.5} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z" />
    </svg>
  );
}

function XIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 20 20" strokeWidth={2} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
    </svg>
  );
}

function FilterDropdown({
  group,
  activeValue,
  onSelect,
}: {
  group: FilterGroup;
  activeValue: string | undefined;
  onSelect: (option: FilterOption) => void;
}) {
  const [open, setOpen] = useState(false);
  const [dropdownSearch, setDropdownSearch] = useState('');
  const ref = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
        setDropdownSearch('');
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (open && inputRef.current) {
      inputRef.current.focus();
    }
  }, [open]);

  const filtered = group.options.filter((o) =>
    o.label.toLowerCase().includes(dropdownSearch.toLowerCase()),
  );

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        className={clsx(
          'inline-flex items-center gap-1 rounded-lg border px-3 py-1.5 text-sm transition-colors',
          activeValue
            ? 'border-brand-300 bg-brand-50 text-brand-700 font-medium'
            : 'border-border bg-white text-text-secondary hover:bg-surface-secondary hover:text-text-primary',
        )}
        onClick={() => setOpen(!open)}
      >
        {group.label}
        <ChevronDownIcon className="h-3.5 w-3.5" />
      </button>

      {open && (
        <div className="absolute left-0 top-full z-50 mt-1 w-56 rounded-lg border border-border bg-white shadow-lg">
          {group.options.length > 5 && (
            <div className="border-b border-border p-2">
              <input
                ref={inputRef}
                type="text"
                className="w-full rounded-md border border-border bg-surface-secondary px-2.5 py-1.5 text-sm text-text-primary placeholder:text-text-tertiary focus:border-brand-400 focus:outline-none focus:ring-1 focus:ring-brand-400/30"
                placeholder={`Search ${group.label.toLowerCase()}...`}
                value={dropdownSearch}
                onChange={(e) => setDropdownSearch(e.target.value)}
              />
            </div>
          )}
          <div className="max-h-52 overflow-y-auto py-1">
            {filtered.length === 0 ? (
              <p className="px-3 py-2 text-sm text-text-tertiary">No results</p>
            ) : (
              filtered.map((option) => (
                <button
                  key={option.id}
                  type="button"
                  className={clsx(
                    'flex w-full items-center gap-2 px-3 py-2 text-left text-sm transition-colors',
                    activeValue === option.id
                      ? 'bg-brand-50 text-brand-700 font-medium'
                      : 'text-text-primary hover:bg-surface-secondary',
                  )}
                  onClick={() => {
                    onSelect(option);
                    setOpen(false);
                    setDropdownSearch('');
                  }}
                >
                  {activeValue === option.id && (
                    <svg className="h-3.5 w-3.5 shrink-0 text-brand-600" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="m4.5 12.75 6 6 9-13.5" />
                    </svg>
                  )}
                  <span className={activeValue === option.id ? '' : 'pl-5.5'}>{option.label}</span>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export function CatalogSearchFilter({
  search,
  onSearchChange,
  activeFilters,
  onFilterAdd,
  onFilterRemove,
  onFiltersClear,
  filterGroups,
}: CatalogSearchFilterProps) {
  const [showAddFilter, setShowAddFilter] = useState(false);
  const addFilterRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (addFilterRef.current && !addFilterRef.current.contains(e.target as Node)) {
        setShowAddFilter(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleCancel = useCallback(() => {
    onSearchChange('');
    onFiltersClear();
  }, [onSearchChange, onFiltersClear]);

  const visibleGroups = filterGroups.filter((g) => g.options.length > 0);

  const defaultFilterKeys = ['category', 'brand', 'tag'];
  const displayedGroups = visibleGroups.filter(
    (g) => defaultFilterKeys.includes(g.key) || activeFilters.some((f) => f.key === g.key),
  );
  const additionalGroups = visibleGroups.filter(
    (g) => !defaultFilterKeys.includes(g.key) && !activeFilters.some((f) => f.key === g.key),
  );

  const hasActiveState = search.length > 0 || activeFilters.length > 0;

  return (
    <div className="space-y-2.5">
      {/* Search Bar */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1">
          <SearchIcon className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-text-tertiary" />
          <input
            type="text"
            className="w-full rounded-lg border border-border bg-white py-2 pl-9 pr-3 text-sm text-text-primary placeholder:text-text-tertiary transition-colors focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-400/20"
            placeholder="Searching all products"
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
          />
        </div>
        {hasActiveState && (
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="text-sm text-text-secondary hover:text-text-primary transition-colors"
              onClick={handleCancel}
            >
              Cancel
            </button>
            <button
              type="button"
              disabled
              className="rounded-lg border border-border bg-white px-3 py-2 text-sm font-medium text-text-tertiary cursor-not-allowed opacity-50"
              title="Coming soon"
            >
              Save as
            </button>
            <button
              type="button"
              className="rounded-lg border border-border bg-white p-2 text-text-secondary transition-colors hover:bg-surface-secondary"
              aria-label="Sort"
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 7.5 7.5 3m0 0L12 7.5M7.5 3v13.5m13.5-4.5L16.5 16.5m0 0L12 12m4.5 4.5V3" />
              </svg>
            </button>
          </div>
        )}
      </div>

      {/* Filter Chips Row */}
      <div className="flex flex-wrap items-center gap-2">
        {displayedGroups.map((group) => {
          const active = activeFilters.find((f) => f.key === group.key);
          return (
            <FilterDropdown
              key={group.key}
              group={group}
              activeValue={active?.value}
              onSelect={(option) => {
                if (active?.value === option.id) {
                  onFilterRemove(group.key);
                } else {
                  onFilterAdd({
                    key: group.key,
                    label: group.label,
                    value: option.id,
                    displayValue: option.label,
                  });
                }
              }}
            />
          );
        })}

        {/* Add Filter button */}
        {additionalGroups.length > 0 && (
          <div ref={addFilterRef} className="relative">
            <button
              type="button"
              className="inline-flex items-center gap-1 rounded-lg border border-dashed border-border bg-white px-3 py-1.5 text-sm text-text-tertiary transition-colors hover:border-border-hover hover:text-text-secondary"
              onClick={() => setShowAddFilter(!showAddFilter)}
            >
              Add filter +
            </button>

            {showAddFilter && (
              <div className="absolute left-0 top-full z-50 mt-1 w-48 rounded-lg border border-border bg-white py-1 shadow-lg">
                {additionalGroups.map((group) => (
                  <button
                    key={group.key}
                    type="button"
                    className="flex w-full items-center px-3 py-2 text-left text-sm text-text-primary transition-colors hover:bg-surface-secondary"
                    onClick={() => {
                      onFilterAdd({
                        key: group.key,
                        label: group.label,
                        value: '',
                        displayValue: '',
                      });
                      setShowAddFilter(false);
                    }}
                  >
                    {group.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Active filter tags (removable) */}
        {activeFilters
          .filter((f) => f.displayValue)
          .map((filter) => (
            <span
              key={filter.key}
              className="inline-flex items-center gap-1 rounded-full bg-brand-50 px-2.5 py-1 text-xs font-medium text-brand-700"
            >
              {filter.label}: {filter.displayValue}
              <button
                type="button"
                className="ml-0.5 rounded-full p-0.5 transition-colors hover:bg-brand-100"
                onClick={() => onFilterRemove(filter.key)}
              >
                <XIcon className="h-3 w-3" />
              </button>
            </span>
          ))}

        {activeFilters.length > 1 && (
          <button
            type="button"
            className="text-xs text-text-tertiary transition-colors hover:text-text-secondary"
            onClick={onFiltersClear}
          >
            Clear all
          </button>
        )}
      </div>
    </div>
  );
}

export type { FilterGroup, ActiveFilter, FilterOption };
